/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useContext } from "react";
import {
  Autocomplete,
  Button,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import DeleteIcon from "@mui/icons-material/Delete";
import MUIDataTable from "mui-datatables";
import { CacheContext } from "../../Context/cacheContext";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { deleteData } from "../../Services/deleteData";
import { urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { tableLayoutOnMobile } from "../../Utils/Constants";
import { clientDocType } from "../../Utils/autocompleteEnum";
import { BuyBackDocName, getFileExtensionByFileType, isMobile } from "../../Utils/utils";
import { ClientData } from "./Clients";
import AntecontractFields, {
  AntecontractFieldsData,
} from "../Generic/AntecontractFields";
import BuyBackFields, { BuyBackFieldsData } from "../Generic/BuyBackFields";
import DocumentsForClients from "./DocumentsForClients";
import VehicleSearchModal from "../Generic/VehicleSearchModal";
import PreviewPDF from "../Generic/PDFViewer";
import PreviewImage from "../Generic/PreviewImage";
import DownloadDoc from "../VehicleDasboard/DownloadDoc";
import { FileDataInfo } from "../../Utils/FilePreview";
import GenericModal from "../../Modal/GenericModal";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { toast } from "react-toastify";

interface ClientDocumentsPanelProps {
  editedClient: ClientData | null | undefined;
}

const initialAntecontractData: AntecontractFieldsData = {
  firma: null,
  advanceInvoiceNumber: "",
  advancePaymentDate: null,
  carPrice: "",
  advanceValue: "",
  remainingPayment: "",
  exchangeRate: "",
  vehicleId: "",
  vehicleName: "",
  VIN: "",
};

const initialBuyBackData: BuyBackFieldsData = {
  brand: null,
  model: null,
  vin: "",
  engineCapacity: "",
  yearOfManufacture: "",
  licensePlateNumber: "",
  itpExpirationDate: null,
  mileage: "",
  vehicleIdentityCardNumber: "",
  priceRon: "",
};

export default function ClientDocumentsPanel({
  editedClient,
}: ClientDocumentsPanelProps) {
  const cacheContext = useContext(CacheContext);

  const [clientDocs, setClientDocs] = useState<any[]>([]);
  const [docId, setDocId] = useState<any>(null);
  const [isSignature, setIsSignature] = useState<any>(null);
  const [firmaId, setFirmaId] = useState<any>(null);
  const [renderOptionsValue, setRenderOptionsValue] = useState("0");
  const [openUploadDoc, setOpenUploadDoc] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [deletedId, setDeletedId] = useState<any>(null);
  const [filePath, setFilePath] = useState<string | undefined>(undefined);
  const [openVehicleSearch, setOpenVehicleSearch] = useState(false);
  const [antecontractData, setAntecontractData] =
    useState<AntecontractFieldsData>(initialAntecontractData);
  const [buyBackData, setBuyBackData] =
    useState<BuyBackFieldsData>(initialBuyBackData);
  const [clientFilePathForGDPR, setClientFilePathForGDPR] = useState<any>(null);
  const [openGDPRModal, setOpenGDPRModal] = useState(false);

  const schema = Yup.object().shape({
    tip_fisier: Yup.object().required("Acest camp este obligatoriu!"),
  });

  const { control, watch, handleSubmit, reset }: any = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    fetchDocs();
  }, [editedClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function fetchDocs() {
    if (!editedClient?.id) return;
    getData(`${urlEnum.clientsDocuments}/${editedClient.id}`)
      .then((res) => setClientDocs(res?.data?.clientsDocs || []))
      .catch((_err) => { /* silently ignore */ });
  }

  async function handleOpenGDPR(client: any, firma: any) {
    if (!firma) {
      const fileName = client?.name.split("/").pop() as string;
      const file = `${urlEnum.templates}/getDoc/${fileName}`;
      setClientFilePathForGDPR({
        id: client.client_id,
        file,
        fileName,
        email: client.email,
        filePath: client?.name,
        isSignature: client.isSignature,
      });
      setOpenGDPRModal(true);
    } else {
      const res = await postData(
        `${urlEnum.templates}/generateGDPR/${editedClient?.id}/${firma}`
      );
      const fileName = res.data.client?.name.split("/").pop() as string;
      setDocId(res.data.client?.id);
      const file = `${urlEnum.templates}/getDoc/${fileName}`;
      setClientFilePathForGDPR({
        id: editedClient?.id,
        file,
        fileName,
        email: editedClient?.email,
        filePath: editedClient?.filePath || "",
        isSignature: editedClient?.isSignature,
      });
      setOpenGDPRModal(true);
      fetchDocs();
    }
  }

  async function handleOpenBeforeContract(
    data: AntecontractFieldsData
  ) {
    const remainingPayment =
      (parseFloat(data.carPrice) || 0) - (parseFloat(data.advanceValue) || 0);
    const res = await postData(
      `${urlEnum.templates}/generateBeforeContract/${editedClient?.id}/${data.firma?.id}/${data.vehicleId}`,
      { ...data, remainingPayment: remainingPayment.toString() }
    );
    const fileName = res.data.client?.filePath.split("/").pop() as string;
    const file = `${urlEnum.templates}/getDoc/${fileName}`;
    setClientFilePathForGDPR({
      id: editedClient?.id,
      file,
      fileName,
      email: editedClient?.email,
      filePath: editedClient?.filePath || "",
      isSignature: editedClient?.isSignature,
    });
    setOpenGDPRModal(true);
  }

  async function handleOpenBuyBack(data: BuyBackFieldsData) {
    const res = await postData(
      `${urlEnum.templates}/generateBuyBack/${editedClient?.id}/${antecontractData.firma?.id}`,
      {
        ...data,
        brand: data.brand?.nume || "",
        model: data.model?.nume || "",
      }
    );
    const fileName = res.data.client?.filePath.split("/").pop() as string;
    const file = `${urlEnum.templates}/getDoc/${fileName}`;
    setClientFilePathForGDPR({
      id: editedClient?.id,
      file,
      fileName,
      email: editedClient?.email,
      filePath: editedClient?.filePath || "",
      isSignature: editedClient?.isSignature,
    });
    setOpenGDPRModal(true);
  }

  function previewDocument() {
    if (!clientFilePathForGDPR) return null;
    const extension = getFileExtensionByFileType(
      clientFilePathForGDPR.file || ""
    );

    if (extension === "pdf" || extension === "PDF") {
      return (
        <PreviewPDF
          doc={docId}
          id={clientFilePathForGDPR.id}
          firmaId={firmaId}
          title={clientFilePathForGDPR.file}
          open={openGDPRModal}
          onClose={() => {
            setOpenGDPRModal(false);
            setClientFilePathForGDPR(null);
          }}
          emailData={{
            to: clientFilePathForGDPR.email || "",
            subject: "Semnarea acordului GDPR",
            message: "GDPR",
            docPath: clientFilePathForGDPR.filePath || "",
          }}
          signature={true}
          isSign={clientFilePathForGDPR.isSignature === "1" ? false : true}
          url={clientFilePathForGDPR.file}
          resetClientFilePathForGDPR={(client: ClientData | null) => {
            if (client) {
              fetchDocs();
              setOpenGDPRModal(false);
              setClientFilePathForGDPR(null);
            }
          }}
        />
      );
    }

    if (
      extension === "jpg" ||
      extension === "png" ||
      extension === "jpeg" ||
      extension === "webp" ||
      extension === "gif" ||
      extension === "svg"
    ) {
      return (
        <PreviewImage
          title={clientFilePathForGDPR.file}
          open={openGDPRModal}
          onClose={() => {
            setOpenGDPRModal(false);
            setClientFilePathForGDPR(null);
          }}
          url={clientFilePathForGDPR.file}
        />
      );
    }

    return null;
  }

  function domElementClick(path: string) {
    const extension = getFileExtensionByFileType(path);

    if (extension === "pdf" || extension === "PDF") {
      return (
        <PreviewPDF
          doc={docId}
          id={editedClient?.id}
          firmaId={firmaId ? firmaId : watch("firma")?.id}
          title={path}
          open={!!path}
          onClose={() => {
            setFilePath(undefined);
          }}
          emailData={{
            to: editedClient?.email || "",
            subject: "Semnarea acordului GDPR",
            message: "GDPR",
            docPath: editedClient?.filePath || "",
          }}
          signature={true}
          isSign={isSignature === "1" ? false : true}
          url={path}
          resetClientFilePathForGDPR={(client: ClientData | null) => {
            if (client) {
              fetchDocs();
              setFilePath(undefined);
            }
          }}
        />
      );
    }

    if (
      extension === "jpg" ||
      extension === "png" ||
      extension === "jpeg" ||
      extension === "webp" ||
      extension === "gif" ||
      extension === "svg"
    ) {
      return (
        <PreviewImage
          title={path}
          open={!!path}
          onClose={() => {
            setFilePath(undefined);
          }}
          url={path}
        />
      );
    }

    if (
      extension === "docx" ||
      extension === "doc" ||
      extension === "xlsx" ||
      extension === "xls"
    ) {
      const fileDataInfo: FileDataInfo = {
        name: path.split("/").pop(),
        path,
        type: extension,
      };
      return <DownloadDoc fileData={fileDataInfo} />;
    }

    return null;
  }

  async function deleteClientFiles(id: any) {
    const url = `${urlEnum.clientFiles}/${id}`;
    const res = (await deleteData(url)) as any;
    if (!res || res?.error) {
      return;
    } else {
      fetchDocs();
      setOpenModalDelete(false);
    }
  }

  function handleChangeToggle(_e: any, newValue: any) {
    if (newValue === null) return;
    setRenderOptionsValue(newValue);
    if (newValue === "0") fetchDocs();
  }

  const handleAntecontractChange = (
    field: keyof AntecontractFieldsData,
    value: unknown
  ) => {
    setAntecontractData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBuyBackChange = (
    field: keyof BuyBackFieldsData,
    value: unknown
  ) => {
    setBuyBackData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectVehicle = (vehicle: any) => {
    setAntecontractData((prev) => ({
      ...prev,
      vehicleId: vehicle.id,
      vehicleName:
        `${vehicle.brand_r?.nume || ""} ${vehicle.model_r?.nume || ""}`.trim(),
      VIN: vehicle.vin || "",
    }));
    setOpenVehicleSearch(false);
  };

  const resetAntecontractData = () => {
    setAntecontractData(initialAntecontractData);
  };

  const onError = (data: any) => {
    // eslint-disable-next-line no-console
    console.log(data);
  };

  const onSubmit = async () => {
    if (!antecontractData.firma) {
      toast.error(Vocabulary.requiredField + ": " + Vocabulary.nume_firma);
      return;
    }
    switch (watch("tip_fisier")?.nume) {
      case "GDPR":
        await handleOpenGDPR(editedClient, antecontractData.firma.id);
        reset({ tip_fisier: null });
        resetAntecontractData();
        break;
      case BuyBackDocName:
        await handleOpenBuyBack(buyBackData);
        break;
      default: // Antecontract
        if (!antecontractData.vehicleId) {
          toast.error(Vocabulary.associateWithVehicle);
          return;
        }
        await handleOpenBeforeContract(antecontractData);
        fetchDocs();
        break;
    }
  };

  const columns = [
    {
      name: "id",
      label: " ",
      options: {
        display: false,
      },
    },
    {
      name: "name",
      label: "Nume fisier",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "file",
      label: "",
      options: {
        display: false,
        filter: true,
        sort: false,
      },
    },
    {
      name: "vehicul",
      label: "Vehicul asociat",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "sendEmailAt",
      label: "Data email",
      options: {
        filter: true,
        sort: false,
        boolean: true,
        customBodyRender: (value: any) => (
          <div>
            {value === "0" ? null : (
              <Tooltip title={"Data email"}>
                <Typography>{value}</Typography>
              </Tooltip>
            )}
          </div>
        ),
      },
    },
    {
      name: "isSignature",
      label: "Semnat",
      options: {
        filter: true,
        sort: false,
        boolean: true,
        customBodyRender: (value: any) => (
          <div>
            {value === "0" ? (
              <Tooltip title={"Nesemnat"}>
                <CircleIcon style={{ color: "#7a7a7a63" }} />
              </Tooltip>
            ) : (
              <Tooltip title={"Semnat"}>
                <CircleIcon style={{ color: "#49ab518c" }} />
              </Tooltip>
            )}
          </div>
        ),
      },
    },
    {
      name: "file",
      label: " ",
      options: {
        filter: true,
        sort: false,
        display: true,
        customBodyRender: (fileName: any, rowIndex: any) => {
          if (fileName === null) return null;
          const path = `${urlEnum.vehicles}/${fileName}`;
          return (
            <>
              <Button
                style={{ float: "right", fontSize: "10px" }}
                size="small"
                color="primary"
                variant="contained"
                onClick={() => {
                  setFilePath(path);
                  setFirmaId(clientDocs[rowIndex?.rowIndex]?.firmaId);
                  setDocId(clientDocs[rowIndex?.rowIndex]?.id);
                  setIsSignature(clientDocs[rowIndex?.rowIndex]?.isSignature);
                }}
              >
                {Vocabulary.open}
              </Button>
            </>
          );
        },
      },
    },
    {
      label: " ",
      name: "file",
      options: {
        filter: false,
        sort: false,
        display: true,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRenderLite: (rowIndex: any) => (
          <div style={{ float: "right", display: "flex" }}>
            <DeleteIcon
              style={{ height: 30, cursor: "pointer", color: "#4449" }}
              onClick={() => {
                setOpenModalDelete(true);
                setDeletedId(clientDocs[rowIndex]);
              }}
            />
          </div>
        ),
      },
    },
  ];

  const options = {
    filter: false,
    viewColumns: false,
    download: false,
    responsive: tableLayoutOnMobile,
    sort: false,
    selectableRows: false as any,
    search: false,
    print: false,
    textLabels: {
      selectedRows: {
        text: "selectate",
        delete: "Sterge",
        deleteAria: "Sterge liniile selectate",
      },
      body: {
        noMatch: "Nu exista date!",
      },
    },
  };

  const renderOptions = (item: string) => {
    switch (item) {
      case "1":
        return (
          <div>
            <form
              onSubmit={handleSubmit(onSubmit, onError)}
              id="clientDocForm"
              method="get"
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile() ? "column" : "row",
                  marginBottom: 10,
                  gap: 5,
                }}
              >
                <Controller
                  name="tip_fisier"
                  control={control}
                  defaultValue={null}
                  render={({
                    field: { ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Autocomplete
                      freeSolo={false}
                      style={{ width: "100%" }}
                      {...field}
                      size="small"
                      getOptionLabel={(option: any) => option?.nume}
                      disableClearable
                      disablePortal
                      id="tip_fisier"
                      onChange={(_event, value) => field.onChange(value)}
                      options={clientDocType}
                      renderInput={(params) => (
                        <TextField
                          error={!!error}
                          helperText={error?.message}
                          label="Tip fisier"
                          name="tip_fisier"
                          type="search"
                          inputRef={ref}
                          {...params}
                        />
                      )}
                    />
                  )}
                />
                {watch("tip_fisier")?.nume === "GDPR" ||
                watch("tip_fisier")?.nume === BuyBackDocName ? (
                  <Autocomplete
                    freeSolo={false}
                    style={{ width: "100%" }}
                    size="small"
                    disablePortal
                    value={antecontractData.firma}
                    onChange={(_, value) => {
                      handleAntecontractChange("firma", value);
                    }}
                    getOptionLabel={(option: any) => option?.nume_firma || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    options={cacheContext.cache?.company || []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={Vocabulary.nume_firma}
                        required
                      />
                    )}
                  />
                ) : null}
              </div>
              {watch("tip_fisier")?.nume === "Antecontract" ? (
                <AntecontractFields
                  data={antecontractData}
                  onChange={handleAntecontractChange}
                  onOpenVehicleSearch={() => setOpenVehicleSearch(true)}
                  showFirma={true}
                />
              ) : null}
              {watch("tip_fisier")?.nume === BuyBackDocName ? (
                <BuyBackFields data={buyBackData} onChange={handleBuyBackChange} />
              ) : null}
              <Button
                variant="contained"
                style={{ color: "white" }}
                color="primary"
                type="submit"
                id="clientDocForm"
              >
                {Vocabulary.generate}
              </Button>
            </form>
          </div>
        );

      default:
        return (
          <div style={{ width: "100%" }}>
            <MUIDataTable
              key={""}
              title={""}
              data={clientDocs}
              columns={columns}
              options={options}
            />
          </div>
        );
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <ToggleButtonGroup
          exclusive
          aria-label="Render Buttons"
          onChange={handleChangeToggle}
        >
          <Tooltip title={Vocabulary.seeTableTooltip} placement="top">
            <ToggleButton
              value="0"
              style={{
                backgroundColor: renderOptionsValue === "0" ? "#ff6633ab" : "#fff",
              }}
            >
              {Vocabulary.allFiles}
            </ToggleButton>
          </Tooltip>
          <Tooltip title={Vocabulary.generateTooltip} placement="top">
            <ToggleButton
              value="1"
              style={{
                backgroundColor: renderOptionsValue === "1" ? "#ff6633ab" : "#fff",
              }}
            >
              {Vocabulary.generate}
            </ToggleButton>
          </Tooltip>
          <Tooltip title={Vocabulary.add} placement="top">
            <ToggleButton value="2" onClick={() => setOpenUploadDoc(true)}>
              {Vocabulary.add}
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </div>

      {renderOptions(renderOptionsValue)}

      {filePath && domElementClick(filePath)}
      {openGDPRModal && clientFilePathForGDPR && previewDocument()}

      <DocumentsForClients
        key=""
        editedClient={editedClient}
        open={openUploadDoc}
        onClose={() => setOpenUploadDoc(false)}
        title={Vocabulary.uploadDoc}
        type="test"
        getNewData={fetchDocs}
      />

      <VehicleSearchModal
        open={openVehicleSearch}
        onClose={() => setOpenVehicleSearch(false)}
        onSelectVehicle={handleSelectVehicle}
      />

      <GenericModal
        open={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        title=""
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 20 }}>
            Sunteti sigur ca doriti sa stergeti {deletedId?.name}?
          </p>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
              variant="contained"
              fullWidth
              color="secondary"
              style={{ margin: 10 }}
              onClick={() => setOpenModalDelete(false)}
            >
              {Vocabulary.no}
            </Button>
            <Button
              onClick={() => deleteClientFiles(deletedId?.id)}
              fullWidth
              variant="contained"
              style={{ color: "white", margin: 10 }}
            >
              {Vocabulary.yes}
            </Button>
          </div>
        </div>
      </GenericModal>
    </>
  );
}
